import { ArrowRight, Shield, Code, Search, Lock, Cloud, Terminal, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import { useEffect } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SectionDivider from '@/components/SectionDivider';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Code className="w-6 h-6" />,
      title: "Application Security",
      description: "Comprehensive security testing and code analysis to protect your applications from vulnerabilities. From SAST to DAST, we ensure your code is secure.",
      link: "/services/application-security",
      features: ["Static Code Analysis", "Dynamic Testing", "Dependency Scanning", "Security Code Review"],
      color: "#00a86b",
      bgColor: "rgba(0, 168, 107, 0.08)",
      borderColor: "rgba(0, 168, 107, 0.30)",
      hoverBg: "rgba(0, 168, 107, 0.15)",
      shadowColor: "rgba(0, 168, 107, 0.20)"
    },
    {
      icon: <Terminal className="w-6 h-6" />,
      title: "DevSecOps",
      description: "Embed security into your CI/CD pipeline. Incorporate prevention in Shift left with automated security testing that doesn't slow down your development velocity.",
      link: "/services/devsecops",
      features: ["CI/CD Security", "Pipeline Integration", "Security Automation", "Compliance as Code"],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.08)",
      borderColor: "rgba(6, 182, 212, 0.30)",
      hoverBg: "rgba(6, 182, 212, 0.15)",
      shadowColor: "rgba(6, 182, 212, 0.20)"
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "Offensive Security",
      description: "Elite offensive security testing to identify vulnerabilities before attackers do. Web, mobile, network, and cloud security testing.",
      link: "/services/offensive-security",
      features: ["Web App Testing", "Network Testing", "Cloud Security Testing", "Red Team Operations"],
      color: "#fbbf24",
      bgColor: "rgba(251, 191, 36, 0.08)",
      borderColor: "rgba(251, 191, 36, 0.30)",
      hoverBg: "rgba(251, 191, 36, 0.15)",
      shadowColor: "rgba(251, 191, 36, 0.20)"
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud Security",
      description: "Comprehensive cloud security services including architecture review, penetration testing, compliance readiness, and DevSecOps integration across multi-cloud environments.",
      link: "/services/cloud-security",
      features: ["Architecture Review", "Cloud Penetration Testing", "Compliance Readiness", "DevSecOps Integration"],
      color: "#3b82f6",
      bgColor: "rgba(59, 130, 246, 0.08)",
      borderColor: "rgba(59, 130, 246, 0.30)",
      hoverBg: "rgba(59, 130, 246, 0.15)",
      shadowColor: "rgba(59, 130, 246, 0.20)"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Security Advisory Services",
      description: "Strategic security advisory services to build mature security programs with expert guidance, governance frameworks, and comprehensive risk management.",
      link: "/services/security-advisory",
      features: ["Risk Assessment", "Vulnerability Management", "Threat Intelligence", "Governance Risk & Compliance"],
      color: "#a855f7",
      bgColor: "rgba(168, 85, 247, 0.08)",
      borderColor: "rgba(168, 85, 247, 0.30)",
      hoverBg: "rgba(168, 85, 247, 0.15)",
      shadowColor: "rgba(168, 85, 247, 0.20)"
    }
  ];

  return (
    <PageLayout>
      <SEO 
        title="Cybersecurity Services | CIPHER Security - Penetration Testing, DevSecOps & More"
        description="Comprehensive cybersecurity services including penetration testing, DevSecOps consulting, application security, cloud security, and vulnerability assessments. Expert-led security solutions for enterprises worldwide."
        keywords={[
          'cybersecurity services',
          'penetration testing services',
          'DevSecOps consulting',
          'application security testing',
          'cloud security services',
          'vulnerability assessment',
          'security consulting',
          'offensive security',
          'security audit services',
          'compliance auditing',
          'incident response services',
          'security advisory',
          'enterprise cybersecurity',
          'cybersecurity company'
        ]}
      />
      <StructuredData type="service" />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 70%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-4 sm:px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(0, 168, 107, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block animate-gradient-text"
                  style={{
                    background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #fbbf24 100%)",
                    backgroundSize: "300% 300%",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#00a86b",
                    fontWeight: 700,
                    filter: "drop-shadow(0 0 8px rgba(0, 168, 107, 0.3))",
                  }}
                >
                  CIPHER Security Services
                </span>
                <span className="block mt-2 text-base sm:text-lg md:text-2xl" style={{ color: "#e5e7eb" }}>
                  Focused Excellence in Cybersecurity
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto text-base sm:text-lg px-4 sm:px-0"
                style={{
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                We don't do everything in cyber, but what we do, we do exceptionally well. 
                Our subject matter experts deliver specialized security services that protect your critical assets.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section
        className="py-16 px-4 sm:px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                className="text-2xl sm:text-3xl md:text-4xl"
                style={{
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Our Security Services Portfolio
              </h2>
              <p
                className="text-base sm:text-lg px-4 sm:px-0"
                style={{
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                Expert cybersecurity services designed to protect your organization from modern threats
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.1,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                >
                  <Link to={service.link} style={{ textDecoration: 'none' }}>
                    <Card
                    className="h-full group cursor-pointer relative overflow-hidden"
                    style={{
                      background: service.bgColor,
                      backdropFilter: "blur(8px)",
                      border: `1px solid ${service.borderColor}`,
                      borderRadius: 16,
                      padding: 0,
                      transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                      transformStyle: "preserve-3d",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = service.hoverBg;
                      e.currentTarget.style.borderColor = service.color;
                      e.currentTarget.style.boxShadow = `0 20px 40px ${service.shadowColor}`;
                      e.currentTarget.style.transform = "translateY(-8px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = service.bgColor;
                      e.currentTarget.style.borderColor = service.borderColor;
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.transform = "translateY(0px)";
                    }}
                  >
                    <CardContent className="p-6 sm:p-8">
                      <div className="flex flex-col sm:flex-row items-start mb-6">
                        <div
                          className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-4"
                          style={{
                            width: 64,
                            height: 64,
                            padding: 16,
                            borderRadius: 12,
                            backgroundColor: `${service.color}20`,
                            color: service.color,
                            boxShadow: `0 4px 16px ${service.color}20`,
                            transition: "transform 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                            e.currentTarget.style.filter = `drop-shadow(0 0 20px ${service.color})`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.filter = "none";
                          }}
                        >
                          {service.icon}
                        </div>
                        <div className="flex-1">
                          <h3
                            className="text-xl sm:text-2xl"
                            style={{
                              fontWeight: 700,
                              color: "#fff",
                              marginBottom: 8,
                            }}
                          >
                            {service.title}
                          </h3>
                          <p
                            className="text-sm sm:text-base"
                            style={{
                              color: "#d1d5db",
                              lineHeight: 1.6,
                              marginBottom: 16,
                            }}
                          >
                            {service.description}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <div
                            key={idx}
                            className="flex items-center text-xs sm:text-sm"
                            style={{
                              color: "#9ca3af",
                            }}
                          >
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                background: service.color,
                                borderRadius: "50%",
                                marginRight: 8,
                                flexShrink: 0,
                              }}
                            />
                            {feature}
                          </div>
                        ))}
                      </div>

                      <div
                        className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                        style={{
                          color: service.color,
                          fontSize: 16,
                          fontWeight: 600,
                        }}
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                  </Link>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Custom CSS for animations */}
      <style>
        {`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-4px);
            }
          }

          .animate-gradient-text {
            animation: gradientShift 6s ease-in-out infinite, float 4s ease-in-out infinite;
          }

          .animate-gradient-text:hover {
            animation: gradientShift 2s ease-in-out infinite, float 2s ease-in-out infinite;
            filter: drop-shadow(0 0 16px rgba(0, 168, 107, 0.5)) !important;
          }
        `}
      </style>
    </PageLayout>
  );
};

export default Services;